import React, { Component, Fragment } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Formik, Field } from 'formik';
import _get from 'lodash/get';
import Grid from '@material-ui/core/Grid';
import GridItem from './../../components/Global/GridItem';

import Button from '@material-ui/core/Button';
import ProgressButton from '../../components/Global/Buttons/Progress';
import Divider from '@material-ui/core/Divider';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorNotification from '../../components/common/ErrorNotification';
import TextFieldInput from '../../components/Global/Inputs/TextField';
import OutlinedSelectInput from '../../components/Global/Inputs/OutlinedSelect';
import TimeFieldInput from '../../components/Global/Inputs/TimeField';
import Autocomplete from '../../components/Global/Inputs/Autocomplete';
import { searchByAddress } from '../../common/maps';
import _pick from 'lodash/pick';
import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import LocationModel from '../../Models/Location';
import LocationFormSchema from './LocationForm.schema';

import * as Queries from '../../graphQL/queries/locationQueries';
import * as Mutations from '../../graphQL/mutations/locationMutations';

import LocationFormStyles from './LocationForm.styles';
import { COUNTRIES, STATES } from '../../constants/dropdowns';

import { AppContext } from '../../AppProvider';

export class LocationForm extends Component {
  state = {
    loading: true,
    error: null,
    location: {},
    errors: null,
    isSubmitting: false,
    address1: ''
  };

  async componentDidMount() {
    try {
      const {
        client: ApolloClient,
        match: {
          params: { id }
        }
      } = this.props;

      if (id) {
        const {
          data: {
            location: { byId: location }
          }
        } = await ApolloClient.query({
          query: Queries.byId,
          variables: { id }
        });

        this.setState({
          location
        });
      } else {
        const { defaultCountry } = this.context.globalProps.orgInfo.config;
        const location = { ...LocationModel };
        location.address.country = defaultCountry;
        this.setState({ location });
      }
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  }

  _handleOnSave = async locationValues => {
    const { client: ApolloClient, history } = this.props;
    try {
      this.setState({ isSubmitting: true });
      const mutation = locationValues.id
        ? Mutations.updateLocationMutation
        : Mutations.createLocationMutation;

      const {
        data: { location }
      } = await ApolloClient.mutate({
        mutation,
        variables: { location: this._formatLocationInput(locationValues) }
      });

      history.push(
        `/locations/${_get(location, 'update.id') ||
          _get(location, 'create.id')}`
      );
      return true;
    } catch (error) {
      this.setState({
        errors: error.message
      });
      return false;
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _formatLocationInput = locationValues => {
    const { currentOrgId, orgInfo, user } = this.context.globalProps;
    const fieldsToPersist = [
      'name',
      'id',
      'rowVersion',
      'externalId',
      'openFromTimeLocal',
      'openToTimeLocal',
      'address.address1',
      'address.address2',
      'address.city',
      'address.postalCode',
      'address.state',
      'address.country',
      'pickupNotes',
      'deliveryNotes'
    ];
    const location = _pick(locationValues, fieldsToPersist);

    const { id, rowVersion, ...values } = location;
    const locationInput = {
      ...values
    };
    if (id) {
      locationInput['id'] = id;
      locationInput['rowVersion'] = `${rowVersion}`;
    } else {
      locationInput['orgId'] = currentOrgId;
      locationInput['divisionId'] = orgInfo.divisionId;
      locationInput['timeZoneId'] = user.timeZoneId;
    }

    return locationInput;
  };

  _handleOnClose = () => {
    const { history } = this.props;
    const {
      location: { id: locationId }
    } = this.state;

    history.push(locationId ? `/locations/${locationId}` : '/locations');
  };

  _getSuggestions = async value => {
    const suggestionResults = await searchByAddress(value);
    const suggestions = suggestionResults.map(result => {
      return {
        label: result.freeformAddress,
        ...result
      };
    });

    return Promise.resolve(suggestions);
  };

  _onAddressSelected = (address, setFieldValue) => {
    setFieldValue('address.address1', `${address.streetNameAndNumber}`);
    setFieldValue('address.address2', '');
    setFieldValue('address.country', address.countryCode);
    setFieldValue('address.state', address.countrySubdivision);
    setFieldValue('address.postalCode', address.postalCode);
    setFieldValue('address.city', address.municipality);
  };

  _onAddressChanged = (change, setFieldValue) => {
    setFieldValue(`address.address1`, change);

    if (!change) {
      setFieldValue('address.country', '');
      setFieldValue('address.state', '');
      setFieldValue('address.postalCode', '');
      setFieldValue('address.city', '');
    }
  };

  _onCountryChanged = (change, setFieldValue) => {
    setFieldValue(`address.country`, change.target.value);

    // Only clear out State on Country change, because those values to
    // choose from change when changing countries
    setFieldValue('address.state', '', false);
  };

  render() {
    const { t, classes } = this.props;

    if (this.state.loading) {
      return <LoadingIndicator />;
    }

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    if (this.state.loading) {
      return <LoadingIndicator />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {this.state.location.id
              ? `${t('Edit Location')} ${this.state.location.name}`
              : t('Add Location')}
          </h1>
        </div>
        <Divider className={classes.divider} />
        <Formik
          validationSchema={LocationFormSchema({ t })}
          enableReinitialize={true}
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{
            ...this.state.location
          }}
          onSubmit={async (values, actions) => {
            const saved = await this._handleOnSave(values);
            if (saved) {
              actions.resetForm();
            }
          }}
          onReset={(values, actions) => {
            actions.resetForm();
            this._handleOnClose();
          }}
          render={({
            values,
            handleChange,
            handleSubmit,
            handleReset,
            setFieldValue,
            dirty
          }) => (
            <Fragment>
              <div className={classnames(classes.wrapper, classes.formWrapped)}>
                <h2
                  className={classnames(classes.headline7, classes.formTitle)}
                >
                  {t('Location Details')}
                </h2>
                <Grid container spacing={16}>
                  <GridItem lg={6}>
                    <Field
                      name={'name'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('Location Name')}
                            value={values.name}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'externalId'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('External ID')}
                            value={values.externalId}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'address.address1'}
                      render={({ field, form }) => {
                        return (
                          <Autocomplete
                            getSuggestions={this._getSuggestions}
                            onSuggestionSelected={selectedAddress =>
                              this._onAddressSelected(
                                selectedAddress,
                                setFieldValue
                              )
                            }
                            label={t('Address Line 1')}
                            value={values.address.address1}
                            onChange={change =>
                              this._onAddressChanged(change, setFieldValue)
                            }
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'address.address2'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('Address Line 2')}
                            value={values.address.address2}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>

                  <GridItem lg={3}>
                    <Field
                      name={'address.country'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('Country')}
                            value={values.address.country}
                            options={COUNTRIES}
                            field={field}
                            form={form}
                            onChange={change =>
                              this._onCountryChanged(change, setFieldValue)
                            }
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name={'address.city'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('City')}
                            value={values.address.city}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name={'address.state'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('State')}
                            value={values.address.state}
                            options={
                              STATES[_get(values, `address.country`, '')] || []
                            }
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={3}>
                    <Field
                      name={'address.postalCode'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('ZIP/Postal Code')}
                            value={values.address.postalCode}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'openFromTimeLocal'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TimeFieldInput
                            value={values.openFromTimeLocal}
                            label={t('Dock Opens')}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'openToTimeLocal'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TimeFieldInput
                            value={values.openToTimeLocal}
                            label={t('Dock Closes')}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={12}>
                    <Field
                      name={'pickupNotes'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('Pickup Notes')}
                            value={values.pickupNotes}
                            multiline={true}
                            classes={{
                              root: classes.moMarginBottom
                            }}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem lg={12}>
                    <Field
                      name={'deliveryNotes'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('Delivery Notes')}
                            value={values.deliveryNotes}
                            multiline={true}
                            classes={{
                              root: classes.moMarginBottom
                            }}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                </Grid>
                <div className={classes.actionsWrapper}>
                  <Button
                    color={'primary'}
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    classes={{
                      label: classes.buttonLabel
                    }}
                    onClick={handleReset}
                  >
                    {t('Cancel')}
                  </Button>
                  <ProgressButton
                    color={'primary'}
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    classes={{
                      label: classes.buttonLabel,
                      disabled: classnames(
                        classes.buttonDisabled,
                        classes.actionButtonDisabled
                      )
                    }}
                    disabled={!dirty}
                    onClick={handleSubmit}
                    isSubmitting={this.state.isSubmitting}
                  >
                    {this.state.location.id
                      ? t('Save Location')
                      : t('Add Location')}
                  </ProgressButton>
                </div>
              </div>
            </Fragment>
          )}
        />
        {this.state.errors && <ErrorNotification message={this.state.errors} />}
      </div>
    );
  }
}

LocationForm.contextType = AppContext;
const LocationFormWithTranslation = withNamespaces()(LocationForm);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    LocationFormStyles
  )
)(withApollo(LocationFormWithTranslation));
